import {
  BaseQueryFn,
  EndpointDefinitions,
  QueryArgFrom,
  ResultTypeFrom,
} from "@reduxjs/toolkit/query/react"
import {
  Api,
  ApiEndpointQuery,
  CoreModule,
  DefinitionType,
  EndpointDefinition,
  Module,
  QueryDefinition,
} from "@reduxjs/toolkit/query"

import { useDispatch as rrUseDispatch } from "react-redux"
import React from "react"
import { ThunkDispatch, UnknownAction } from "@reduxjs/toolkit"

export const prewarmModuleName = Symbol()
export type PrewarmModule = typeof prewarmModuleName

type UsePrewarm<Definition extends QueryDefinition<any, any, any, any, any>> = (
  prewarmDataFactory: () => PrewarmData<Definition>,
  prewarmDataDeps: React.DependencyList,
) => void

type PrewarmData<Definition extends QueryDefinition<any, any, any, any, any>> =
  Map<QueryArgFrom<Definition>, ResultTypeFrom<Definition>>

declare module "@reduxjs/toolkit/query" {
  export interface ApiModules<
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    BaseQuery extends BaseQueryFn,
    Definitions extends EndpointDefinitions,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ReducerPath extends string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    TagTypes extends string,
  > {
    [prewarmModuleName]: {
      endpoints: {
        [K in keyof Definitions]: Definitions[K] extends QueryDefinition<
          any,
          any,
          any,
          any,
          any
        >
          ? {
              usePrewarm: UsePrewarm<Definitions[K]>
            }
          : never
      }
    } & {
      [K in keyof Definitions as Definitions[K] extends {
        type: DefinitionType.query
      }
        ? `use${Capitalize<K & string>}Prewarm`
        : never]: UsePrewarm<
        Extract<Definitions[K], QueryDefinition<any, any, any, any>>
      >
    }
  }
}

const ENDPOINT_QUERY: `${DefinitionType}` = "query"

function isQueryDefinition(
  e: EndpointDefinition<any, any, any, any, any, any, any>,
): e is QueryDefinition<any, any, any, any, any, any> {
  return e.type === ENDPOINT_QUERY
}

function capitalize(str: string) {
  return str.replace(str[0], str[0].toUpperCase())
}

type RR = typeof import("react-redux")

export const prewarmModule = (
  options: { hooks: { useDispatch: RR["useDispatch"] } } = {
    hooks: { useDispatch: rrUseDispatch },
  },
): Module<PrewarmModule> => ({
  name: prewarmModuleName,
  init(api) {
    const {
      hooks: { useDispatch },
    } = options
    return {
      injectEndpoint(endpointName, definition) {
        const anyApi = api as any as Api<
          any,
          Record<string, any>,
          string,
          string,
          PrewarmModule | CoreModule
        >

        if (isQueryDefinition(definition)) {
          const usePrewarm: UsePrewarm<typeof definition> = (
            prewarmDataFactory,
            prewarmDataDeps,
          ) => {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            const prewarmData = React.useMemo(
              prewarmDataFactory,
              prewarmDataDeps,
            )

            const dispatch =
              useDispatch<ThunkDispatch<any, any, UnknownAction>>()
            const querySubscriptions = React.useMemo(() => {
              dispatch(
                anyApi.util.upsertQueryEntries(
                  Array.from(prewarmData.entries()).map(([arg, value]) => ({
                    endpointName,
                    arg,
                    value,
                  })),
                ),
              )

              return Array.from(prewarmData.keys()).map((key) =>
                dispatch(
                  (
                    anyApi.endpoints[endpointName] as ApiEndpointQuery<any, any>
                  ).initiate(key),
                ),
              )
            }, [dispatch, prewarmData])

            React.useEffect(
              () => () =>
                querySubscriptions.forEach((querySubscription) =>
                  querySubscription.unsubscribe(),
                ),
              [querySubscriptions],
            )
          }
          anyApi.endpoints[endpointName].usePrewarm = usePrewarm
          anyApi[
            `use${capitalize(endpointName) as Capitalize<typeof endpointName>}Prewarm`
          ] = usePrewarm
        }
      },
    }
  },
})
